// =============================================================================
// MeuDriver — Admin Dispute Management
// Lists problematic bookings and provides a resolution workflow
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate } from '@/lib/utils'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import type { BookingWithDetails } from '@/types/database'
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'
import { Input } from '@/components/ui/input'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RefundOption = 'full' | 'partial' | 'none'

interface ResolveDialogState {
  open: boolean
  booking: BookingWithDetails | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending_payment: 'Aguardando pagamento',
    confirmed: 'Confirmada',
    in_progress: 'Em andamento',
    completed: 'Concluída',
    cancelled_by_student: 'Cancelada pelo aluno',
    cancelled_by_instructor: 'Cancelada pelo instrutor',
    no_show: 'Não compareceu',
  }
  return labels[status] ?? status
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'cancelled_by_student':
    case 'cancelled_by_instructor':
      return 'bg-red-100 text-red-800 border-red-200'
    case 'no_show':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AdminDisputes() {
  const [disputes, setDisputes] = useState<BookingWithDetails[]>([])
  const [loading, setLoading] = useState(true)

  const [resolveDialog, setResolveDialog] = useState<ResolveDialogState>({
    open: false,
    booking: null,
  })
  const [refundOption, setRefundOption] = useState<RefundOption>('full')
  const [resolutionNotes, setResolutionNotes] = useState('')
  const [resolving, setResolving] = useState(false)

  // Admin manual refund state
  const [refundDialog, setRefundDialog] = useState<{ open: boolean; booking: BookingWithDetails | null }>({
    open: false,
    booking: null,
  })
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState('')
  const [refunding, setRefunding] = useState(false)

  // ── fetch disputed bookings ─────────────────────────────────────────────────

  async function fetchDisputes() {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(
          `*, student:profiles!bookings_student_id_fkey(*), instructor:instructors!bookings_instructor_id_fkey(*, profile:profiles(*)), payment:payments(*)`,
        )
        .in('status', ['no_show', 'cancelled_by_student', 'cancelled_by_instructor'])
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      setDisputes((data as unknown as BookingWithDetails[]) ?? [])
    } catch {
      toast.error('Erro ao carregar disputas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDisputes()
  }, [])

  // ── open resolve dialog ─────────────────────────────────────────────────────

  function openResolve(booking: BookingWithDetails) {
    setRefundOption('full')
    setResolutionNotes('')
    setResolveDialog({ open: true, booking })
  }

  // ── handle resolution ───────────────────────────────────────────────────────

  async function handleResolve() {
    const booking = resolveDialog.booking
    if (!booking) return

    setResolving(true)
    try {
      const totalPrice = booking.total_price ?? 0

      // payments is a one-to-many join — take the most recent payment row
      const payment = Array.isArray(booking.payment)
        ? booking.payment[0] ?? null
        : booking.payment

      let refundAmount = 0
      let refundPercent = 0
      let newPaymentStatus: 'refunded' | 'paid' | 'failed' = 'paid'

      if (refundOption === 'full') {
        refundAmount = totalPrice
        refundPercent = 100
        newPaymentStatus = 'refunded'
      } else if (refundOption === 'partial') {
        refundAmount = totalPrice * 0.5
        refundPercent = 50
        newPaymentStatus = 'refunded'
      }

      const resolvedAt = new Date().toISOString()

      // Update payment record when a refund applies
      if (refundOption !== 'none' && payment?.id) {
        const { error: paymentError } = await supabase
          .from('payments')
          .update({
            status: newPaymentStatus,
            refunded_at: resolvedAt,
          })
          .eq('id', payment.id)

        if (paymentError) throw paymentError
      }

      // Persist resolution note + resolved_at to the disputes row (if one exists)
      await supabase
        .from('disputes')
        .update({
          status: 'resolved',
          resolution_note: resolutionNotes.trim() || null,
          resolved_at: resolvedAt,
        })
        .eq('booking_id', booking.id)

      toast.success(
        refundOption === 'none'
          ? 'Disputa resolvida sem reembolso.'
          : `Disputa resolvida. Reembolso de ${formatCurrency(refundAmount)} (${refundPercent}%) registrado.`,
      )

      setResolveDialog({ open: false, booking: null })
      await fetchDisputes()
    } catch {
      toast.error('Erro ao resolver disputa')
    } finally {
      setResolving(false)
    }
  }

  // ── admin manual refund via edge function ───────────────────────────────────

  async function handleAdminRefund() {
    const booking = refundDialog.booking
    if (!booking) return
    const amount = refundAmount ? parseFloat(refundAmount) : undefined
    if (amount !== undefined && (isNaN(amount) || amount <= 0)) {
      toast.error('Informe um valor de reembolso válido.')
      return
    }
    setRefunding(true)
    try {
      const { data: sessionData } = await (await import('@/lib/supabase')).supabase.auth.getSession()
      const jwt = sessionData.session?.access_token
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({
          booking_id: booking.id,
          amount,
          reason: refundReason.trim() || undefined,
        }),
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result.error ?? 'Erro ao reembolsar')
      toast.success('Reembolso processado com sucesso!')
      setRefundDialog({ open: false, booking: null })
      setRefundAmount('')
      setRefundReason('')
      await fetchDisputes()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro inesperado')
    } finally {
      setRefunding(false)
    }
  }

  // ── render ──────────────────────────────────────────────────────────────────

  const activeBooking = resolveDialog.booking

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Gestão de Disputas</h1>
        <p className="mt-1 text-muted-foreground">
          Aulas canceladas ou com não comparecimento que requerem atenção.
        </p>
      </div>

      {/* Summary bar */}
      {!loading && (
        <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong>{disputes.length}</strong> aula(s) com status problemático aguardando
            resolução.
          </span>
        </div>
      )}

      {/* Disputes List */}
      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle>Aulas Pendentes de Resolução</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-4 p-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-md" />
              ))}
            </div>
          ) : disputes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CheckCircle2 className="mb-2 h-10 w-10 text-green-500" />
              <p className="font-medium">Nenhuma disputa pendente.</p>
              <p className="text-sm">Todas as aulas estão sem pendências.</p>
            </div>
          ) : (
            <div className="divide-y">
              {disputes.map((booking) => {
                const studentName = booking.student?.full_name ?? '—'
                const instructorName = booking.instructor?.profile?.full_name ?? '—'
                const amount = booking.total_price ?? 0

                return (
                  <div
                    key={booking.id}
                    className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between hover:bg-muted/20 transition-colors"
                  >
                    {/* Info */}
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant="outline"
                          className={getStatusBadgeClass(booking.status)}
                        >
                          {getStatusLabel(booking.status)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          #{booking.id.slice(0, 8)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-sm sm:grid-cols-4">
                        <div>
                          <span className="text-muted-foreground">Aluno: </span>
                          <span className="font-medium">{studentName}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Instrutor: </span>
                          <span className="font-medium">{instructorName}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Data: </span>
                          <span>
                            {formatDate(booking.scheduled_at, "dd/MM/yyyy 'às' HH:mm")}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Valor: </span>
                          <span className="font-semibold">{formatCurrency(amount)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openResolve(booking)}
                      >
                        Resolver
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                        onClick={() => {
                          setRefundAmount(String(booking.total_price ?? ''))
                          setRefundReason('')
                          setRefundDialog({ open: true, booking })
                        }}
                      >
                        <RefreshCw className="h-3 w-3" />
                        Reembolsar
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialog.open}
        onOpenChange={(open) => setResolveDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolver Disputa</DialogTitle>
            <DialogDescription>
              Selecione a política de reembolso para a aula{' '}
              {activeBooking && (
                <span>
                  de <strong>{activeBooking.student?.full_name ?? '—'}</strong> com{' '}
                  <strong>{activeBooking.instructor?.profile?.full_name ?? '—'}</strong>
                  {activeBooking.total_price != null && (
                    <> — {formatCurrency(activeBooking.total_price)}</>
                  )}
                </span>
              )}
              .
            </DialogDescription>
          </DialogHeader>

          {/* Refund options */}
          <div className="space-y-3">
            <Label>Política de Reembolso</Label>
            <div className="space-y-2">
              {(
                [
                  {
                    value: 'full' as RefundOption,
                    label: 'Reembolso integral (100%)',
                    description: 'O aluno recebe o valor total de volta.',
                  },
                  {
                    value: 'partial' as RefundOption,
                    label: 'Reembolso parcial (50%)',
                    description: 'O aluno recebe metade do valor pago.',
                  },
                  {
                    value: 'none' as RefundOption,
                    label: 'Sem reembolso',
                    description: 'Nenhum valor é devolvido ao aluno.',
                  },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRefundOption(option.value)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    refundOption === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <p className="text-sm font-medium">{option.label}</p>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="resolution-notes">Observações (opcional)</Label>
            <Textarea
              id="resolution-notes"
              placeholder="Descreva o motivo ou detalhes da resolução..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Summary */}
          {activeBooking?.total_price != null && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor da aula</span>
                <span>{formatCurrency(activeBooking.total_price)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span className="text-muted-foreground">Reembolso ao aluno</span>
                <span>
                  {refundOption === 'full'
                    ? formatCurrency(activeBooking.total_price)
                    : refundOption === 'partial'
                    ? formatCurrency(activeBooking.total_price * 0.5)
                    : formatCurrency(0)}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResolveDialog({ open: false, booking: null })}
            >
              Cancelar
            </Button>
            <Button disabled={resolving} onClick={handleResolve}>
              {resolving ? 'Resolvendo...' : 'Confirmar Resolução'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Refund Dialog */}
      <Dialog
        open={refundDialog.open}
        onOpenChange={(open) => setRefundDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-orange-500" />
              Reembolso via Mercado Pago
            </DialogTitle>
            <DialogDescription>
              Dispara um reembolso real pelo MP.{' '}
              {refundDialog.booking && (
                <span>
                  Booking de{' '}
                  <strong>{refundDialog.booking.student?.full_name ?? '—'}</strong>
                  {refundDialog.booking.total_price != null && (
                    <> — total {formatCurrency(refundDialog.booking.total_price)}</>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Valor a reembolsar (R$)</Label>
              <Input
                type="number"
                min={0.01}
                step={0.01}
                placeholder={`Deixe em branco para reembolso total`}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Parcial: informe o valor. Total: deixe vazio.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Motivo (opcional)</Label>
              <Textarea
                placeholder="Ex: Instrutor cancelou com menos de 2h de antecedência"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                rows={2}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefundDialog({ open: false, booking: null })}
            >
              Cancelar
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700"
              disabled={refunding}
              onClick={handleAdminRefund}
            >
              {refunding ? 'Processando...' : 'Confirmar Reembolso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
